import React, { createContext, useContext, useState, useEffect } from 'react';

const CustomerAuthContext = createContext();

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Railway backend URL for direct API calls (from env or fallback)
  const RAILWAY_BACKEND_URL = process.env.REACT_APP_RAILWAY_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "https://revstar-main-platform-production.up.railway.app";

  // Platform domain - used to detect platform subdomains vs custom domains
  const PLATFORM_DOMAIN = process.env.REACT_APP_PLATFORM_DOMAIN || 'userevstar.com';

  /**
   * Determine the correct API base URL based on the current environment
   * - Localhost: Use /api (CRA dev server proxy)
   * - Vercel (*.vercel.app): Use /api (Vercel rewrites handle it)
   * - Platform subdomains (*.userevstar.com): Use /api (Vercel custom domain with rewrites)
   * - Custom domains (CloudFront): Call Railway backend directly
   */
  const getBackendUrl = () => {
    const envUrl = process.env.REACT_APP_BACKEND_URL;

    // If explicitly set via env var, use it
    if (envUrl && envUrl.trim() !== "") {
      console.log("[CustomerAuth] Using REACT_APP_BACKEND_URL:", envUrl);
      return envUrl.endsWith("/api") ? envUrl : `${envUrl}/api`;
    }

    // Check current hostname to determine routing
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;

      // Localhost - use proxy
      if (hostname === "localhost" || hostname === "127.0.0.1") {
        console.log("[CustomerAuth] Localhost detected, using /api proxy");
        return "/api";
      }

      // Vercel deployment - use proxy
      if (hostname.endsWith(".vercel.app")) {
        console.log("[CustomerAuth] Vercel deployment detected, using /api proxy");
        return "/api";
      }

      // Platform subdomains (e.g., store.staging.userevstar.com, store.userevstar.com)
      // These are hosted on Vercel with custom domains and have /api rewrites configured
      if (hostname.endsWith(PLATFORM_DOMAIN)) {
        console.log("[CustomerAuth] Platform subdomain detected:", hostname);
        console.log("[CustomerAuth] Using /api proxy (Vercel custom domain with rewrites)");
        return "/api";
      }

      // All other domains (custom domains, CloudFront-served)
      console.log("[CustomerAuth] Custom domain detected:", hostname);
      return `${RAILWAY_BACKEND_URL}/api`;
    }

    return "/api";
  };

  const BACKEND_URL = getBackendUrl();

  // Ensure customer object always has both _id and id for compatibility
  const normalizeCustomer = (raw) => {
    if (!raw) return null;
    const normalizedId = raw._id || raw.id || null;
    return {
      ...raw,
      _id: normalizedId,
      id: normalizedId
    };
  };

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('🔵 CustomerAuth: Starting authentication initialization');

        // Check for sessionId in URL query parameters (for cross-domain auth)
        const urlParams = new URLSearchParams(window.location.search);
        const urlSessionId = urlParams.get('sessionId');

        if (urlSessionId) {
          console.log('🔵 CustomerAuth: SessionId found in URL! Storing to localStorage...');
          localStorage.setItem('customerSessionId', urlSessionId);

          // Clean up URL by removing sessionId parameter
          urlParams.delete('sessionId');
          const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
          window.history.replaceState({}, '', newUrl);
          console.log('🔵 CustomerAuth: URL cleaned, sessionId stored');
        }

        // Get sessionId from localStorage (either just stored from URL or previously saved)
        const sessionId = localStorage.getItem('customerSessionId');
        console.log('🔵 CustomerAuth: SessionId from localStorage:', sessionId ? '✅ FOUND' : '❌ NOT FOUND');

        if (sessionId) {
          console.log('🔵 CustomerAuth: Calling validateSession...');
          // Validate session with backend (this will also set customer data if valid)
          const isValid = await validateSession(sessionId);
          console.log('🔵 CustomerAuth: Session validation result:', isValid ? '✅ VALID' : '❌ INVALID');
          if (!isValid) {
            clearAuth();
          } else {
            // Clear any stale redirect flags when session is valid
            sessionStorage.removeItem('checkoutRedirectedToLogin');
            console.log('🔵 CustomerAuth: Session valid, cleared stale redirect flag');
          }
        } else {
          // CRITICAL FIX: No sessionId in localStorage, but check for cookie-based session
          // This handles cases where localStorage was cleared but HTTP-only cookie still exists
          console.log('🔵 CustomerAuth: No sessionId in localStorage, checking for cookie-based session...');

          try {
            const response = await fetch(`${BACKEND_URL}/customer/auth/refresh`, {
              method: 'POST',
              credentials: 'include' // Send HTTP-only cookies
            });

            if (response.ok) {
              const data = await response.json();
              console.log('🔵 CustomerAuth: Refresh response:', data);

              if (data.success && data.customer && data.sessionId) {
                console.log('✅ CustomerAuth: Found valid cookie-based session! Restoring auth state...');
                // Restore sessionId to localStorage for future use
                localStorage.setItem('customerSessionId', data.sessionId);
                setCustomer(normalizeCustomer(data.customer));
                setIsAuthenticated(true);

                // CRITICAL: Clear any stale redirect flags from previous checkout attempts
                // This prevents redirect loops when user is already authenticated
                sessionStorage.removeItem('checkoutRedirectedToLogin');
                console.log('🔵 CustomerAuth: Cleared stale redirect flag');
              } else {
                console.log('🔵 CustomerAuth: No valid cookie-based session found');
              }
            } else {
              console.log('🔵 CustomerAuth: Refresh endpoint returned error:', response.status);
            }
          } catch (refreshError) {
            console.log('🔵 CustomerAuth: No cookie-based session (expected for guests):', refreshError.message);
          }
        }
      } catch (error) {
        console.error('🔴 CustomerAuth: Error initializing authentication:', error);
        clearAuth();
      } finally {
        console.log('🔵 CustomerAuth: Initialization complete, setting loading to false');
        setLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validate session with backend using HttpOnly cookies
  const validateSession = async (sessionId) => {
    try {
      const url = `${BACKEND_URL}/customer/auth/profile?sessionId=${sessionId}`;
      console.log('🔵 CustomerAuth: Fetching profile from:', url);
      // Use profile endpoint with sessionId for session validation
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include' // This will send HttpOnly cookies
      });

      console.log('🔵 CustomerAuth: Profile response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('🔵 CustomerAuth: Profile response data:', data);
        if (data.success && data.customer) {
          console.log('✅ CustomerAuth: Customer has', data.customer.addresses?.length || 0, 'addresses');
          setCustomer(normalizeCustomer(data.customer));
          setIsAuthenticated(true);
          console.log('✅ CustomerAuth: Session validated with customer data');
          return true;
        } else {
          console.log('❌ CustomerAuth: Response missing success or customer data');
        }
      } else {
        console.log('❌ CustomerAuth: Profile endpoint returned error status');
      }
      return false;
    } catch (error) {
      console.error('🔴 CustomerAuth: Session validation error:', error);
      return false;
    }
  };

  // Fetch customer profile from backend (no localStorage caching)
  const fetchCustomerProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/customer/auth/profile`, {
        method: 'GET',
        credentials: 'include' // This will send HttpOnly cookies
      });

      if (response.ok) {
        const data = await response.json();
        return normalizeCustomer(data.customer);
      }
      return null;
    } catch (error) {
      console.error('CustomerAuth: Error fetching customer profile:', error);
      return null;
    }
  };

  const validateToken = async (tokenToValidate) => {
    try {
      const response = await fetch(`${BACKEND_URL}/customer/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenToValidate}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.customer) {
          // Update customer data with fresh data from backend
          setCustomer(normalizeCustomer(data.customer));
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('CustomerAuth: Token validation error:', error);
      return false;
    }
  };

  const login = async (email, password, storeSlug) => {
    try {
      const response = await fetch(`${BACKEND_URL}/customer/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password,
          storeSlug
        }),
        credentials: 'include' // This enables HttpOnly cookies
      });

      const data = await response.json();

      if (response.ok) {
        // Fetch full customer profile with addresses
        const fullCustomerData = await fetchCustomerProfile();
        if (fullCustomerData) {
          setCustomer(normalizeCustomer(fullCustomerData));
        } else {
          // Fallback to login response data if profile fetch fails
          setCustomer(normalizeCustomer(data.customer));
        }
        setIsAuthenticated(true);

        // Store session ID in localStorage for persistence across page navigations
        if (data.sessionId) {
          localStorage.setItem('customerSessionId', data.sessionId);
          console.log('CustomerAuth: Stored sessionId in localStorage');
        }

        console.log('CustomerAuth: Login successful - using secure session management');
        return { success: true, data };
      } else {
        console.error('CustomerAuth: Login failed:', data.error);
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('CustomerAuth: Login error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const register = async (userData, storeSlug) => {
    try {
      const response = await fetch(`${BACKEND_URL}/customer/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...userData,
          storeSlug
        }),
        credentials: 'include' // This enables HttpOnly cookies
      });

      const data = await response.json();

      if (response.ok) {
        // Fetch full customer profile with addresses
        const fullCustomerData = await fetchCustomerProfile();
        if (fullCustomerData) {
          setCustomer(normalizeCustomer(fullCustomerData));
        } else {
          // Fallback to registration response data if profile fetch fails
          setCustomer(normalizeCustomer(data.customer));
        }
        setIsAuthenticated(true);

        // Store session ID in localStorage for persistence across page navigations
        if (data.sessionId) {
          localStorage.setItem('customerSessionId', data.sessionId);
          console.log('CustomerAuth: Stored sessionId in localStorage');
        }

        console.log('CustomerAuth: Registration successful - using secure session management');
        return { success: true, data };
      } else {
        console.error('CustomerAuth: Registration failed:', data.error);
        return { success: false, error: data.error || 'Registration failed' };
      }
    } catch (error) {
      console.error('CustomerAuth: Registration error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const logout = () => {
    console.log('CustomerAuth: Logging out customer');
    clearAuth();
    // Navigation will be handled by the component using the context
    window.location.href = '/customer/login';
  };

  const clearAuth = () => {
    setToken(null);
    setCustomer(null);
    setIsAuthenticated(false);
    // Only clear session ID, keep guest cart for UX
    localStorage.removeItem('customerSessionId');
    localStorage.removeItem('customerToken'); // Legacy cleanup
    localStorage.removeItem('customerData'); // Legacy cleanup
    localStorage.removeItem('checkout_redirect_cart');
  };

  const refreshCustomerData = async () => {
    try {
      // Always fetch fresh data from backend (no caching)
      const customerData = await fetchCustomerProfile();
      if (customerData) {
        setCustomer(normalizeCustomer(customerData));
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('CustomerAuth: Error refreshing customer data:', error);
      return false;
    }
  };

  const value = {
    customer,
    token,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    clearAuth,
    refreshCustomerData,
    validateToken,
    validateSession
  };

  // Debug context state
  console.log('🟢 === CustomerAuthContext State ===');
  console.log('🟢 Customer:', customer);
  console.log('🟢 Customer Email:', customer?.email || 'None');
  console.log('🟢 Customer Addresses:', customer?.addresses?.length || 0);
  console.log('🟢 Is Authenticated:', isAuthenticated);
  console.log('🟢 Loading:', loading);
  console.log('🟢 =================================');

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
